<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import {
  type Event,
  type EventPreference,
  type EventsOverview,
  type EventsOverviewProduct,
  type Product,
  type ProductPreference,
  SalesMethod,
} from "mws-ts-v2-sdk";

import { queries } from "~/queries";
import { MediaContentTypeV2 } from "~/types/Catalog";

type PropsProduct =
  | Product
  | EventsOverviewProduct
  | (ProductPreference & { isFollowedByUser?: boolean });

type Props = {
  product: PropsProduct;
  event?: Event | EventsOverview | EventPreference;
  showEventName?: boolean;
  eventName?: string;
  enableImageLazyLoading?: boolean;
  eventListIndex?: number;
  trackingSource: string;
};

const {
  event,
  product,
  enableImageLazyLoading = true,
  eventListIndex,
  trackingSource,
} = defineProps<Props>();

type Emits = {
  (e: "product-finished"): void;
  (e: "product-clicked"): void;
};

const emit = defineEmits<Emits>();

const localePath = useLocalePath();

const { productMediaBgClass } = usePhotoStudioBackground(() => product);

const salesMethod = computed(() =>
  isEventOverviewProduct(product) ? SalesMethod.Bidding : product.salesMethod,
);

const productName = computed(() => {
  // we want to show the short name in the product cards
  return isEventOverviewProduct(product)
    ? product.name
    : (product.shortName ?? product.name);
});

const productFinished = computed(() =>
  isEventOverviewProduct(product) ? product.isFinished : product.finished,
);

const productPrices = computed(() =>
  isEventOverviewProduct(product)
    ? product.price
      ? [product.price]
      : []
    : product.prices,
);

const productLabels = computed(() =>
  isEventOverviewProduct(product)
    ? product.labels
    : product.labels.map((label) => label.text),
);

const { amount: currentPrice } = usePrice(productPrices);

const hasFixedPrice = computed(() => {
  return salesMethod.value === SalesMethod.FixedPrice;
});

const isConfigurable = computed(() => {
  if ("customizable" in product) {
    return product.customizable;
  }

  return false;
});

const isInStock = computed(() => {
  return isEventOverviewProduct(product) ? true : product.inStock;
});

const priceDisplayType = computed(() => {
  if (salesMethod.value === SalesMethod.Bidding) return "bid";
  else if (!isInStock.value) return "soldOut";
  else if (isConfigurable.value) return "from";
  else return "fixed";
});

const productMedia = computed(() => {
  if (isEventOverviewProduct(product))
    return {
      type: MediaContentTypeV2.Image,
      url: product.productImageUrl,
      metadata: null,
    };

  if (!product.media || product.media.length === 0) {
    return;
  }

  return product.media[0];
});

const priceInEuros = computed(() => {
  if (isEventOverviewProduct(product)) {
    return product.euroPrice?.price;
  }

  return product.prices.find(({ currency }) => currency === "EUR")?.price;
});

const emitProductFinished = () => {
  emit("product-finished");
};

const { addProductClick } = useAnalytics();

const route = useRoute();
const navigateEcommerce = async () => {
  emit("product-clicked");

  addProductClick({
    product: product,
    labels: productLabels.value,
    salesMethod: salesMethod.value,
    euroPrice: priceInEuros.value ?? 0,
    activeCurrencyPrice: currentPrice.value.value,
    eventData: event,
    eventListIndex: eventListIndex,
    source_path: route.path,
    source: trackingSource,
  });
};

const queryClient = useQueryClient();

const platformStore = usePlatformStore();
const platformId = computed(
  () => platformStore.getPlatformConfiguration.platformId,
);

const prefetchProductDetail = () => {
  queryClient.prefetchQuery({
    ...queries.products.details(product.slug, platformId),
    staleTime: 60000,
  });
};

const productCardLink = ref();
useDebounceHover(
  productCardLink,
  () => {
    prefetchProductDetail();
  },
  { delayEnter: 300 },
);

const isFrameTheGame = computed(() => platformStore.isFrameTheGame);

const { t } = useI18n();
const imageAltTag = computed(() => {
  const teamName = event?.participants.find((t) => t.isFeatured)?.name;
  const productName = product.name;

  if (!teamName) return productName;

  // approximation for `{athleteName} {teamName} shirt`
  // we don't have athleteName and the product type in the product object here
  return t("product.meta.footballShirts.imageAltTag", {
    PLACEHOLDER_athleteName: productName,
    PLACEHOLDER_teamName: teamName,
  });
});
</script>

<template>
  <div
    class="product-card"
    data-cy="product-card"
    data-test="product-card"
    :data-id="product.id"
  >
    <ProductFollowButton
      v-if="typeof product.isFollowedByUser === 'boolean'"
      :id="product.id"
      :name="product.name"
      :is-followed-by-user="product.isFollowedByUser"
      class="absolute right-2 top-2"
    />

    <NuxtLink
      ref="productCardLink"
      class="product-card__link-container"
      data-cy="product-card-link"
      draggable="false"
      :to="
        localePath({
          path: `/product/${product.slug}`,
        })
      "
      @focus="prefetchProductDetail"
      @click="navigateEcommerce"
    >
      <div>
        <div class="product-card__top-container">
          <ProductCardDetails
            :product="product"
            :has-fixed-price="hasFixedPrice"
          />
          <div
            class="product-card__image-container"
            :class="[productMediaBgClass, { 'p-7 md:p-9': isFrameTheGame }]"
          >
            <div
              v-if="!productMedia || !productMedia.type || !productMedia.url"
              class="product-card__media"
            />
            <NuxtImg
              v-else-if="productMedia.type === MediaContentTypeV2.Image"
              provider="imgix"
              preset="productCard"
              :alt="imageAltTag"
              width="375"
              height="375"
              class="product-card__media product-card__image"
              :loading="enableImageLazyLoading ? 'lazy' : 'eager'"
              :src="productMedia.url"
              @error="setBrokenMediaFallback"
            />
            <BaseVideoPlayer
              v-else-if="productMedia.type === MediaContentTypeV2.Video"
              :src="productMedia.url"
              class="product-card__media product-card__video"
            />
          </div>
        </div>

        <div class="product-card__bottom-container">
          <div
            v-if="
              salesMethod === SalesMethod.Bidding ||
              salesMethod === SalesMethod.FixedPrice
            "
            class="flex flex-col gap-1"
          >
            <span
              class="line-clamp-2 text-wrap text-platform-text-1 font-semibold"
              :title="productName ?? undefined"
              data-test="product-card-name"
            >
              {{ productName }}
            </span>
            <span
              v-if="showEventName && eventName"
              class="uppercase text-platform-text-2 text-xs"
            >
              {{ eventName }}
            </span>
            <div class="product-card__price-section">
              <span
                v-if="priceDisplayType === 'soldOut'"
                data-test="product-card__price-section--sold-out"
                class="product-card__price-section--fixed-price"
              >
                {{ $t("product.card.soldOut") }}
              </span>

              <I18nT
                v-else-if="priceDisplayType === 'from'"
                scope="global"
                keypath="product.card.from"
                tag="div"
              >
                <template #PLACEHOLDER_price>
                  <Transition
                    name="fade"
                    mode="out-in"
                  >
                    <BaseCurrency
                      :key="currentPrice.value"
                      :iso="currentPrice.currencyCode"
                      :value="currentPrice.value"
                      class="font-bold"
                    />
                  </Transition>
                </template>
              </I18nT>
              <span
                v-if="priceDisplayType === 'fixed'"
                class="font-bold"
              >
                <Transition
                  name="fade"
                  mode="out-in"
                >
                  <BaseCurrency
                    :key="currentPrice.value"
                    :iso="currentPrice.currencyCode"
                    :value="currentPrice.value"
                  />
                </Transition>
              </span>
              <template v-if="priceDisplayType === 'bid'">
                <ProductCardBidDisplay
                  :current-price="currentPrice"
                  :product="product"
                  :is-finished="productFinished"
                />
              </template>
            </div>
          </div>

          <BaseCountdownTimer
            v-if="product.endDate && !hasFixedPrice"
            :end-date="product.endDate"
            :finished="productFinished"
            class="inline-block p-0 text-sm"
            data-cy="countdown-timer"
            data-test="countdown-timer"
            :use-text-format="true"
            :use-color-coding="true"
            precision="relative"
            @timer-finished="emitProductFinished"
          />
        </div>
      </div>
    </NuxtLink>
  </div>
</template>

<style lang="postcss" scoped>
.product-card {
  @apply relative;

  &--can-be-revealed {
    perspective: 75rem;

    /* padding to make the minimum height = 100% of the width */
    padding-top: 100%;
  }

  &__top-container {
    aspect-ratio: 1;
    @apply mb-2 relative flex justify-center items-center;
    @apply bg-platform-bg-2;
  }

  &__image-container {
    aspect-ratio: 4/5;
    @apply max-w-full w-full;
    @apply flex overflow-hidden justify-center;
  }

  &__media {
    @apply object-contain;
    min-width: 100%;

    /* Quick on the way out */
    transition: ease 0.25s;
  }
  &__image {
    @apply self-center;
  }

  &__media:hover {
    transform: scale(1.05);

    /* Slower on the way in */
    transition: ease 0.5s;
  }

  &__link-container {
    @apply flex flex-col flex-grow;
    cursor: pointer;
  }

  &__bottom-container {
    @apply flex flex-col flex-grow gap-1;
  }

  &__latest-bid {
    @apply font-bold text-2xl;
  }

  &__price-section {
    @apply text-base;
  }
}
</style>
